import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Pause, Play, Plus, Speech, Trash2, Volume2 } from "lucide-react";
import { Button } from "../ui/button";
import { api, errMessage } from "../../lib/api";
import { SpeechRecords } from "./SpeechRecords";
import { BetaFeedbackCard, MonthlySnapshot, ParentBadges, PracticeTrend } from "./SpeechExtras";

const LEVEL_NAMES = ["Listen", "See how", "The sound", "Syllables", "Words",
  "Phrases", "Sentences", "Story time"];

// Parents pick practice targets for each child, choose a level and weekly
// frequency, pause/remove goals and add approved custom words. Goals never
// change on their own — only a parent (or an authorized SLP) edits them.
export const SpeechGoals = () => {
  const [children, setChildren] = useState([]);
  const [childId, setChildId] = useState(null);
  const [goals, setGoals] = useState([]);
  const [library, setLibrary] = useState([]);
  const [summary, setSummary] = useState(null);
  const [pick, setPick] = useState("");
  const [beta, setBeta] = useState(null);
  const [betaCode, setBetaCode] = useState("");
  const [customWord, setCustomWord] = useState({});
  const [presenters, setPresenters] = useState([]);
  const [previewing, setPreviewing] = useState(""); // presenter_id playing now

  // Parent voice preview: fetch the coach intro audio (cached HD TTS) in the
  // child's practice language and play it so parents can compare voices.
  const hearVoice = async (e, pid) => {
    e.stopPropagation(); // don't also select the coach
    setPreviewing(pid);
    try {
      const { data } = await api.post(`/speech/avatar-presenters/${pid}/voice-preview`,
        { lang: currentChild?.language || "en" }, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const audio = new Audio(url);
      audio.onended = () => { setPreviewing(""); URL.revokeObjectURL(url); };
      audio.onerror = () => { setPreviewing(""); URL.revokeObjectURL(url); };
      await audio.play();
    } catch (err) { setPreviewing(""); toast.error(errMessage(err)); }
  };

  useEffect(() => {
    api.get("/speech/avatar-presenters")
      .then(({ data }) => setPresenters(data.presenters)).catch(() => {});
  }, []);

  useEffect(() => {
    api.get("/children").then(({ data }) => {
      const kids = data.filter((c) => c.status === "active");
      setChildren(kids);
      if (kids.length) setChildId(kids[0].child_id);
    }).catch(() => setChildren([]));
    api.get("/beta/status").then(({ data }) => setBeta(data)).catch(() => setBeta(null));
  }, []);

  const redeemBeta = async () => {
    if (!betaCode.trim()) return;
    try {
      const { data } = await api.post("/beta/redeem", { code: betaCode });
      toast.success(data.message);
      setBeta({ beta_active: true, premium: true });
      setBetaCode("");
    } catch (e) { toast.error(errMessage(e)); }
  };

  const load = (cid) => {
    api.get(`/speech/children/${cid}/goals`).then(({ data }) => {
      setGoals(data.goals);
      setLibrary(data.library);
    }).catch(() => {});
    api.get(`/speech/children/${cid}/summary`).then(({ data }) => setSummary(data)).catch(() => {});
  };

  useEffect(() => { if (childId) load(childId); }, [childId]);

  const assign = async () => {
    if (!pick) return;
    try {
      await api.post(`/speech/children/${childId}/goals`, { content_id: pick });
      toast.success("Practice target assigned");
      setPick("");
      load(childId);
    } catch (e) { toast.error(errMessage(e)); }
  };

  const patch = async (goalId, body, okMsg) => {
    try {
      await api.patch(`/speech/goals/${goalId}`, body);
      toast.success(okMsg);
      load(childId);
    } catch (e) { toast.error(errMessage(e)); }
  };

  const remove = async (goalId) => {
    try {
      await api.delete(`/speech/goals/${goalId}`);
      toast.success("Target removed");
      load(childId);
    } catch (e) { toast.error(errMessage(e)); }
  };

  if (!children.length) return null;
  const available = library.filter((l) => !goals.some((g) => g.content_id === l.content_id && g.status !== "removed"));
  const currentChild = children.find((c) => c.child_id === childId);

  const pickAvatar = async (pid) => {
    try {
      await api.put(`/speech/children/${childId}/avatar`, { presenter_id: pid });
      setChildren(children.map((c) => (c.child_id === childId ? { ...c, avatar_presenter: pid } : c)));
      toast.success("Coach updated — their intro plays at the next practice");
    } catch (e) { toast.error(errMessage(e)); }
  };

  const toggleCustom = async (enabled) => {
    try {
      await api.put(`/speech/children/${childId}/avatar-custom`, { enabled });
      setChildren(children.map((c) => (c.child_id === childId ? { ...c, avatar_custom_enabled: enabled } : c)));
      toast.success(enabled
        ? "Custom coach on — your child can restyle their coach (colors only, never shared)"
        : "Custom coach off — back to the standard look");
    } catch (e) { toast.error(errMessage(e)); }
  };

  const setLanguage = async (language) => {
    try {
      await api.patch(`/children/${childId}`, { language });
      setChildren(children.map((c) => (c.child_id === childId ? { ...c, language } : c)));
      load(childId);
      toast.success({
        en: "Practice language set to English",
        es: "Español añadido — English targets stay available too",
      }[language]);
    } catch (e) { toast.error(errMessage(e)); }
  };

  return (
    <div className="bg-card rounded-2xl border border-border p-5 space-y-4" data-testid="speech-goals">
      <div>
        <p className="text-sm font-bold flex items-center gap-2">
          <Speech className="h-4 w-4 text-secondary" aria-hidden="true" /> Speech &amp; language practice
        </p>
        <p className="text-xs text-muted-foreground mt-1">
          Choose what your child practices with their coach. If voice practice is off in your
          consent settings, the coach runs in listen-only mode — practice is never blocked.
        </p>
      </div>

      {children.length > 1 && (
        <div className="flex gap-2 flex-wrap" role="tablist" aria-label="Choose child">
          {children.map((c) => (
            <button key={c.child_id} type="button" onClick={() => setChildId(c.child_id)}
              className={`rounded-full px-3 py-1.5 text-xs font-bold border transition-colors ${
                childId === c.child_id ? "bg-secondary text-secondary-foreground border-secondary" : "border-border"}`}
              data-testid={`speech-goals-child-${c.child_id}`}>
              {c.display_name}
            </button>
          ))}
        </div>
      )}

      {presenters.length > 0 && (
        <div className="space-y-1.5" data-testid="avatar-picker">
          <p className="text-[11px] font-bold">3D practice coach &amp; voice — tap the speaker to hear each coach</p>
          <div className="flex gap-2 flex-wrap">
            {presenters.map((p) => {
              const sel = (currentChild?.avatar_presenter || "maya") === p.presenter_id;
              return (
                <div key={p.presenter_id}
                  className={`rounded-xl border px-3 py-2 transition-colors flex items-center gap-2 ${
                    sel ? "border-secondary bg-secondary/10" : "border-border hover:bg-muted"}`}
                  data-testid={`avatar-card-${p.presenter_id}`}>
                  <button type="button" onClick={() => pickAvatar(p.presenter_id)}
                    className="flex items-center gap-2 text-left"
                    data-testid={`avatar-pick-${p.presenter_id}`}>
                    {p.photo_url ? (
                      <img src={p.photo_url} alt={p.name} loading="lazy"
                        className="h-10 w-10 rounded-full object-cover border border-border shrink-0"
                        data-testid={`avatar-photo-${p.presenter_id}`} />
                    ) : (
                      <span className="h-10 w-10 rounded-full bg-secondary/15 text-secondary font-bold text-sm flex items-center justify-center shrink-0" aria-hidden="true">
                        {p.name.split(" ").pop()[0]}
                      </span>
                    )}
                    <span>
                      <span className="text-xs font-bold block">{p.name}</span>
                      <span className="text-[10px] text-muted-foreground">
                        {p.summary}{p.ready ? "" : " · voice warming up"}
                      </span>
                    </span>
                  </button>
                  <button type="button" onClick={(e) => hearVoice(e, p.presenter_id)}
                    disabled={previewing === p.presenter_id}
                    className="shrink-0 h-8 w-8 rounded-full border border-border flex items-center justify-center hover:bg-secondary/15 disabled:opacity-50 transition-colors"
                    aria-label={`Hear ${p.name}'s voice`}
                    title={`Hear ${p.name}'s voice`}
                    data-testid={`voice-preview-${p.presenter_id}`}>
                    <Volume2 className={`h-4 w-4 ${previewing === p.presenter_id ? "animate-pulse text-secondary" : ""}`} aria-hidden="true" />
                  </button>
                </div>
              );
            })}
          </div>
          <label className="flex items-center gap-2 text-[11px] font-semibold cursor-pointer"
            data-testid="avatar-custom-gate">
            <input type="checkbox" className="h-3.5 w-3.5 accent-primary"
              checked={Boolean(currentChild?.avatar_custom_enabled)}
              onChange={(e) => toggleCustom(e.target.checked)}
              data-testid="avatar-custom-gate-checkbox" />
            Let {currentChild?.display_name || "my child"} restyle their coach
            (fixed colors only — never photos, never shared)
          </label>
        </div>
      )}

      {beta && !beta.premium && !beta.beta_active && (
        <div className="flex items-center gap-2 flex-wrap rounded-lg border border-dashed border-border px-2.5 py-2"
          data-testid="beta-redeem-box">
          <p className="text-[11px] font-bold">Have a beta invite code?</p>
          <input value={betaCode} onChange={(e) => setBetaCode(e.target.value)} maxLength={40}
            placeholder="PIPBETA-XXXXXX"
            className="rounded-md border border-border bg-background px-2 py-1 text-[11px] w-36 uppercase"
            data-testid="beta-redeem-input" />
          <button type="button" onClick={redeemBeta}
            className="rounded-full bg-secondary text-secondary-foreground px-3 py-1 text-[11px] font-bold"
            data-testid="beta-redeem-button">
            Join the beta
          </button>
          <p className="text-[10px] text-muted-foreground w-full">
            Beta families get complimentary Premium practice features during the beta — no billing starts automatically.
          </p>
        </div>
      )}
      {beta?.beta_active && (
        <p className="text-[11px] font-bold text-emerald-700" data-testid="beta-active-badge">
          Beta family — complimentary Premium during the beta period
        </p>
      )}
      <BetaFeedbackCard beta={beta}
        onSubmitted={() => setBeta({ ...beta, feedback_submitted_this_week: true })} />

      <ParentBadges childId={childId} childName={currentChild?.display_name || "Our practicer"} />
      <MonthlySnapshot childId={childId} />
      <PracticeTrend childId={childId} />

      <div className="flex items-center gap-2 flex-wrap" data-testid="speech-language-picker">
        <p className="text-[11px] font-bold">Practice language</p>
        {[["en", "English"], ["es", "Español"]].map(([code, label]) => (
          <button key={code} type="button" onClick={() => setLanguage(code)}
            className={`rounded-full px-3 py-1 text-[11px] font-bold border transition-colors ${
              (currentChild?.language || "en") === code
                ? "bg-secondary text-secondary-foreground border-secondary" : "border-border hover:bg-muted"}`}
            data-testid={`speech-language-${code}`}>
            {label}
          </button>
        ))}
        {(currentChild?.language || "en") !== "en" && (
          <p className="text-[10px] text-muted-foreground w-full">
            Bilingual mode: {{ es: "Spanish" }[currentChild?.language]} sounds
            appear alongside the English library — the coach speaks both in the same voice.
          </p>
        )}
      </div>

      <div className="flex gap-2 items-center flex-wrap">
        <select value={pick} onChange={(e) => setPick(e.target.value)}
          className="rounded-md border border-border bg-background px-2 py-1.5 text-xs font-semibold flex-1 min-w-40"
          aria-label="Choose a practice target" data-testid="speech-goal-pick">
          <option value="">Add a practice target…</option>
          {[
            ["Core sounds", (l) => l.kind === "sound_target" && (!l.category || l.category === "core")],
            ["Vocalic R", (l) => l.category === "vocalic_r"],
            ["Minimal pairs", (l) => l.category === "minimal_pairs"],
            ["Word games", (l) => l.kind !== "sound_target"],
          ].map(([label, match]) => {
            const opts = available.filter(match);
            if (!opts.length) return null;
            return (
              <optgroup key={label} label={label}>
                {opts.map((l) => (
                  <option key={l.content_id} value={l.content_id}>
                    {l.title + ({ es: " · Español" }[l.language] || "")}
                  </option>
                ))}
              </optgroup>
            );
          })}
        </select>
        <Button size="sm" onClick={assign} disabled={!pick} className="rounded-full gap-1.5 text-xs"
          data-testid="speech-goal-assign">
          <Plus className="h-3.5 w-3.5" aria-hidden="true" /> Assign
        </Button>
      </div>

      <div className="space-y-2">
        {goals.filter((g) => g.status !== "removed").map((g) => {
          const tip = library.find((l) => l.content_id === g.content_id)?.parent_tip;
          return (
          <div key={g.goal_id} className="rounded-xl border border-border p-3 space-y-2"
            data-testid={`speech-goal-${g.goal_id}`}>
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <p className="text-xs font-bold">
                {g.title}
                {g.status === "paused" && (
                  <span className="ml-2 rounded-full bg-amber-100 border border-amber-300 text-amber-800 text-[10px] px-1.5 py-0.5">Paused</span>
                )}
                {g.assigned_by === "professional" && (
                  <span className="ml-2 rounded-full bg-sky-100 border border-sky-300 text-sky-800 text-[10px] px-1.5 py-0.5">SLP assigned</span>
                )}
              </p>
              <div className="flex items-center gap-1.5">
                <button type="button" title={g.status === "paused" ? "Resume" : "Pause"}
                  onClick={() => patch(g.goal_id, { status: g.status === "paused" ? "active" : "paused" },
                    g.status === "paused" ? "Target resumed" : "Target paused")}
                  className="rounded-full border border-border p-1.5 hover:bg-muted"
                  data-testid={`speech-goal-pause-${g.goal_id}`}>
                  {g.status === "paused" ? <Play className="h-3.5 w-3.5" aria-hidden="true" /> : <Pause className="h-3.5 w-3.5" aria-hidden="true" />}
                </button>
                <button type="button" title="Remove" onClick={() => remove(g.goal_id)}
                  className="rounded-full border border-border p-1.5 hover:bg-muted text-red-600"
                  data-testid={`speech-goal-remove-${g.goal_id}`}>
                  <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                </button>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap text-[11px] font-semibold">
              Start at
              <select value={g.level} onChange={(e) => patch(g.goal_id, { level: Number(e.target.value) }, "Level updated")}
                className="rounded-md border border-border bg-background px-1.5 py-1 text-[11px]"
                aria-label="Practice level" data-testid={`speech-goal-level-${g.goal_id}`}>
                {LEVEL_NAMES.map((n, i) => <option key={n} value={i + 1}>{`${i + 1}. ${n}`}</option>)}
              </select>
              ·
              <select value={g.frequency_per_week}
                onChange={(e) => patch(g.goal_id, { frequency_per_week: Number(e.target.value) }, "Frequency updated")}
                className="rounded-md border border-border bg-background px-1.5 py-1 text-[11px]"
                aria-label="Practice frequency" data-testid={`speech-goal-frequency-${g.goal_id}`}>
                {[1, 2, 3, 4, 5, 7].map((n) => <option key={n} value={n}>{`${n}× a week`}</option>)}
              </select>
            </div>
            {tip && (
              <p className="text-[11px] text-muted-foreground rounded-lg bg-amber-50 border border-amber-200 px-2 py-1.5"
                data-testid={`speech-goal-parent-tip-${g.goal_id}`}>
                <b>Parent tip</b>: {tip}
              </p>
            )}
            {g.kind === "sound_target" && (
              <div className="flex items-center gap-2 flex-wrap">
                {(g.custom_words || []).map((w) => (
                  <span key={w} className="rounded-full bg-violet-50 border border-violet-200 text-violet-800 text-[10px] font-bold px-2 py-0.5">{w}</span>
                ))}
                <input value={customWord[g.goal_id] || ""} maxLength={20}
                  onChange={(e) => setCustomWord({ ...customWord, [g.goal_id]: e.target.value })}
                  placeholder="Add a custom word"
                  className="rounded-md border border-border bg-background px-2 py-1 text-[11px] w-32"
                  data-testid={`speech-goal-custom-input-${g.goal_id}`} />
                <Button size="sm" variant="outline" className="rounded-full text-[10px] h-6 px-2"
                  onClick={() => { patch(g.goal_id, { add_custom_word: customWord[g.goal_id] }, "Word added"); setCustomWord({ ...customWord, [g.goal_id]: "" }); }}
                  disabled={!(customWord[g.goal_id] || "").trim()}
                  data-testid={`speech-goal-custom-add-${g.goal_id}`}>
                  Add word
                </Button>
              </div>
            )}
          </div>
        );})}
        {goals.filter((g) => g.status !== "removed").length === 0 && (
          <p className="text-xs text-muted-foreground" data-testid="speech-goals-empty">
            No targets assigned yet — your child sees a small starter set until you choose.
          </p>
        )}
      </div>

      {summary?.targets?.length > 0 && (
        <div className="border-t border-border pt-3 space-y-1.5" data-testid="speech-summary">
          <p className="text-xs font-bold">Last 30 days of practice</p>
          {summary.targets.map((t) => (
            <p key={t.content_id} className="text-[11px] text-muted-foreground">
              <b>{t.title || t.content_id}</b> — {t.sessions} session{t.sessions === 1 ? "" : "s"},
              {" "}{t.listens} listens, {t.my_turns} tries{t.completed ? `, ${t.completed} completed` : ""}
            </p>
          ))}
        </div>
      )}
      <SpeechRecords childId={childId} />
      <p className="text-[10px] text-muted-foreground" data-testid="speech-goals-disclaimer">
        Practice information only — never a diagnosis, clinical evaluation or substitute for a
        speech-language pathologist.
      </p>
    </div>
  );
};
